import axios, { CancelTokenSource } from 'axios';
import { ConsultationsConsumerRenderProps } from 'components/consultations/ConsultationsContext';
import UpsertConsultation from 'components/consultations/UpsertConsultation';
import HomeTableHeaderCell from 'components/layout/HomeTableHeaderCell';
import LoaderComponent from 'components/LoaderComponent';
import { EntriesConsumerRenderProps } from 'components/segments/entires/EntriesContext';
import { TreatmentsConsumerRenderProps } from 'components/treatments/TreatmentsContext';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Icon, Table } from 'semantic-ui-react';
import { getAllConsultationsByTreatmentId } from 'services/consultationServices';
import styled from 'styled-components';
import { BillStatus } from 'ts-types/api.enums';
import { ConsultationDto, PatientDto, TreatmentDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const ConsultationsInfoContainer = styled.div`
  flex-grow: 1;
  cursor: context-menu;
  overflow: auto;
  max-height: 310px;

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }
`;

const StyledTableCellWithIcons = styled(Table.Cell)`
  padding-right: 0 !important;
  text-align: center !important;
`;

interface Props extends MessageSourceProps, EntriesConsumerRenderProps {
  currentPatient: PatientDto,
  currentTreatment: TreatmentDto,
  editTreatment: (treatment: TreatmentDto) => void,
  addConsultation: (consultation: ConsultationDto) => void
  editConsultation: (consultation: ConsultationDto) => void
  deleteConsultation: () => void,
  setCacheSearchId: (searchId?: string) => void
}

interface State {
  loadingInProgress: boolean,
  isCostEstimationComplete: boolean,
  cancelTokenSource: CancelTokenSource
}

interface Props extends MessageSourceProps, ConsultationsConsumerRenderProps, TreatmentsConsumerRenderProps {
}

class ConsultationsInfo extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      loadingInProgress: false,
      isCostEstimationComplete: false,
      cancelTokenSource
    }
  }

  getConsultations = (treatment: TreatmentDto | undefined): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    const {changeConsultations} = this.props;

    const onFinally = () => {
      this.setState({
        loadingInProgress: false
      })
    };

    if (treatment) {
      this.setState({
        loadingInProgress: true
      });
      getAllConsultationsByTreatmentId(treatment.id, cancelTokenSource)
          .then(response => {
            const currentTreatmentProps = this.props.currentTreatment;
            if (!currentTreatmentProps || treatment.id === currentTreatmentProps.id) {
              changeConsultations(response);
            }
          })
          .catch(noop)
          .finally(onFinally);
    } else {
      changeConsultations([]);
    }
  };

  componentDidMount(): void {
    this.mounted = true;
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<any>, snapshot?: any): void {
    const {selectedTreatment, changeConsultations} = this.props;
    if (prevProps.selectedTreatment != selectedTreatment) {
      if (selectedTreatment) {
        this.getConsultations(selectedTreatment);
      } else {
        changeConsultations([]);
      }
    }
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  markConsultation = (consultationId: number) => {
    const {currentConsultations, changeSelectedConsultation} = this.props;

    const foundConsultation = currentConsultations!
        .find((consultation: ConsultationDto) => consultation.id === consultationId);

    changeSelectedConsultation(foundConsultation);
  };

  consultationTableInfoRender(): React.ReactNode {

    const {
      selectedConsultation,
      currentConsultations,
      getMessage,
      currentTreatment
    } = this.props;
    const selectedConsultationId = selectedConsultation ? selectedConsultation.id : -1;

    return (
        <ConsultationsInfoContainer>
          <Table basic='very' celled padded selectable compact>
            <Table.Header>
              <Table.Row>
                <HomeTableHeaderCell>{getMessage('bill')}</HomeTableHeaderCell>
                <HomeTableHeaderCell>{getMessage('date')}</HomeTableHeaderCell>
                <HomeTableHeaderCell>{getMessage('time')}</HomeTableHeaderCell>
              </Table.Row>
            </Table.Header>
            {currentConsultations && (
                <Table.Body>
                  {
                    currentConsultations.map(consultation => {
                      const shouldDisableEdit = BillStatus.BILL  == consultation.billStatus
                          || (currentTreatment && currentTreatment.status)
                          || (currentTreatment && currentTreatment.deleted == 1);

                      return (
                          <Table.Row
                              key={consultation.id}
                              onClick={() => this.markConsultation(consultation.id)}
                              active={consultation.id == selectedConsultationId}
                              style={shouldDisableEdit ? {color: 'grey'} : {}}
                              onDoubleClick={!shouldDisableEdit ? this.props.setConsultationId : noop}
                          >
                            <StyledTableCellWithIcons>
                              {this.renderConsultationBillStatus(consultation.billStatus)}
                            </StyledTableCellWithIcons>
                            <Table.Cell>{moment(consultation.consultationDate, 'YYYY-MM-DD').format("DD.MM.YYYY")}</Table.Cell>
                            <Table.Cell>{moment(consultation.consultationTime, "hh:mm:ss").format(moment.HTML5_FMT.TIME)}</Table.Cell>
                          </Table.Row>
                      );
                    })
                  }
                </Table.Body>
            )}
          </Table>
        </ConsultationsInfoContainer>
    );
  };

  renderConsultationForm(): React.ReactNode {

    const {currentPatient, currentTreatment, addConsultation, editConsultation, consultationFormOpen, consultationId, onCloseUpsertModal, setCacheSearchId} = this.props;
    const {isCostEstimationComplete} = this.state;

    return (
        consultationFormOpen && (
            <UpsertConsultation
                consultationFormOpen={consultationFormOpen}
                isCostEstimationComplete={isCostEstimationComplete}
                onCloseUpsertModal={onCloseUpsertModal}
                currentTreatment={currentTreatment}
                currentPatient={currentPatient}
                consultationId={consultationId}
                addConsultation={addConsultation}
                editConsultation={editConsultation}
                setCacheSearchId={setCacheSearchId}
            />)
    );

  }

  render(): React.ReactNode {

    const {loadingInProgress} = this.state;
    const {getMessage} = this.props;

    if (loadingInProgress) {
      return (
          <LoaderComponent message={getMessage("consultations.loader")}/>
      )
    }
    return (
        <Fragment>
          {this.consultationTableInfoRender()}
          {this.renderConsultationForm()}
        </Fragment>
    );
  }

  renderConsultationBillStatus = (billStatus: any): React.ReactNode => {

    const {selectedTreatment} = this.props;

    if (BillStatus.BILL == billStatus) {
      return <Icon name={"file alternate outline"}/>;
    } else if (BillStatus.INTERMEDIATE_BILL == billStatus) {
      return <Icon name={"file alternate"}/>
    } else if (selectedTreatment && selectedTreatment.status) {
      return <Icon name={"key"}/>
    }

    return <Fragment/>
  }
}

export default withMessages(ConsultationsInfo);
